/**
 * @fileoverview Canonical, system-wide structured U.S. postal address contract
 *   plus its pure (offline-safe) formatting and format-validation helpers.
 * @module core-domain/types/postalAddress
 *
 * Responsibilities:
 * - Define the single shared `PostalAddress` shape used by contacts, delivery
 *   recipients, and any other entity that needs a mailing destination.
 * - Provide pure helpers (`formatPostalAddress`, `isPostalAddressComplete`,
 *   `evaluatePostalAddressFormat`) that work fully offline with no network.
 * Dependencies:
 * - None (intentionally framework- and network-free so every package can reuse
 *   it without taking on dependencies).
 * Architecture:
 * - Local-first: this is the offline source-of-truth shape. Online enrichment
 *   (Mapbox geocode candidates, USPS standardization) lives in
 *   `forms/shared/addressValidation.ts` and only ever fills these same fields.
 * - The delivery-record `InspectionDeliveryPostalAddress` (local-db) is the same
 *   shape; this contract is the canonical lift so the two never diverge.
 * Notes:
 * - Status enum is intentionally three values; "Mapbox-only / offline" maps to
 *   `needsValidation`, USPS-confirmed maps to `validated`, and USPS or local
 *   format problems map to `validationWarning`.
 */

/**
 * @contract PostalAddressValidationStatus
 * Purpose: Local-first validation/enrichment state of a postal address.
 * Fields:
 * - needsValidation: Incomplete, or complete but not yet USPS-confirmed
 *   (includes the Mapbox-only / offline fallback case).
 * - validated: Confirmed deliverable / USPS-standardized.
 * - validationWarning: Complete but failed format or USPS deliverability checks;
 *   needs human review. Never blocks saving.
 * Rules: A warning never erases the user-entered values.
 * Sync: Stored verbatim on the owning document; never derived at read time.
 */
export type PostalAddressValidationStatus = 'needsValidation' | 'validated' | 'validationWarning';

export const POSTAL_VALIDATION_STATUSES: readonly PostalAddressValidationStatus[] = [
  'needsValidation',
  'validated',
  'validationWarning',
];

/**
 * @contract PostalAddress
 * Purpose: Structured U.S. mailing address used system-wide.
 * Fields:
 * - line1: Primary street line (required for mail readiness).
 * - line2: Optional secondary unit/suite line.
 * - city: USPS city name (required for mail readiness).
 * - state: Two-letter state/territory code (required for mail readiness).
 * - postalCode: ZIP or ZIP+4 (required for mail readiness).
 * - countryCode: ISO country code; defaults to 'US'.
 * - formattedAddress: Canonical multi-line display string (USPS-standardized
 *   once validated).
 * - validationStatus: Local-first validation/enrichment state.
 * - validationMessage: Human-readable validation feedback.
 * - validatedAt: ISO timestamp of the latest validation attempt.
 * - latitude/longitude: Optional geocode from Mapbox (informational only).
 * Rules:
 * - line1, city, state, postalCode are required for mail delivery readiness.
 * - Postal compliance warnings never erase user-entered values.
 * Sync:
 * - Persisted on the owning document (contact, delivery record) so the exact
 *   snapshot survives later edits to other records.
 */
export interface PostalAddress {
  line1: string;
  line2?: string;
  city: string;
  state: string;
  postalCode: string;
  countryCode: string;
  formattedAddress?: string;
  validationStatus: PostalAddressValidationStatus;
  validationMessage?: string;
  validatedAt?: string;
  latitude?: number;
  longitude?: number;
}

const ZIP_REGEX = /^\d{5}(?:-\d{4})?$/;
const STATE_REGEX = /^[A-Z]{2}$/;

/**
 * @process formatPostalAddress
 * Steps:
 * 1. Trim each line and join street lines with the "city, ST ZIP" locality line.
 * Inputs: Partial postal address fields.
 * Outputs: Canonical multi-line display string (empty string when no parts).
 * Failure Handling: Pure; never throws.
 * Idempotency: REQUIRED (pure function).
 */
export function formatPostalAddress(address?: Partial<PostalAddress>): string {
  if (!address) return '';
  const line1 = (address.line1 ?? '').trim();
  const line2 = (address.line2 ?? '').trim();
  const city = (address.city ?? '').trim();
  const state = (address.state ?? '').trim().toUpperCase();
  const postalCode = (address.postalCode ?? '').trim();
  const locality = [city, state, postalCode].filter(Boolean).join(', ');
  return [line1, line2, locality].filter(Boolean).join('\n');
}

/**
 * @process isPostalAddressComplete
 * Steps:
 * 1. Verify the four mail-required parts (line1, city, state, postalCode) are
 *    all non-empty after trimming.
 * Inputs: Partial postal address fields.
 * Outputs: boolean — whether the address has the minimum parts for mail.
 * Failure Handling: Pure; never throws.
 * Idempotency: REQUIRED (pure function).
 */
export function isPostalAddressComplete(address?: Partial<PostalAddress>): boolean {
  if (!address) return false;
  return Boolean(
    (address.line1 ?? '').trim() &&
      (address.city ?? '').trim() &&
      (address.state ?? '').trim() &&
      (address.postalCode ?? '').trim(),
  );
}

/**
 * @process evaluatePostalAddressFormat
 * Steps:
 * 1. Normalize fields (trim, uppercase state/country, default country 'US').
 * 2. If any required part is missing → status 'needsValidation'.
 * 3. Else if ZIP or state fail US format → status 'validationWarning'.
 * 4. Else status 'needsValidation' (well-formed, but not yet USPS-confirmed).
 * Inputs: Partial postal address fields.
 * Outputs: A normalized PostalAddress with offline-derived status, or undefined
 *   when no input is provided.
 * Failure Handling: Returns warning/needsValidation instead of throwing.
 * Idempotency: REQUIRED (pure function — caller supplies any timestamp).
 * Notes: Local format check ONLY. 'validated' is reserved for USPS confirmation.
 */
export function evaluatePostalAddressFormat(address?: Partial<PostalAddress>): PostalAddress | undefined {
  if (!address) return undefined;

  const line1 = (address.line1 ?? '').trim();
  const line2 = (address.line2 ?? '').trim();
  const city = (address.city ?? '').trim();
  const state = (address.state ?? '').trim().toUpperCase();
  const postalCode = (address.postalCode ?? '').trim();
  const countryCode = (address.countryCode ?? 'US').trim().toUpperCase() || 'US';
  const base: PostalAddress = {
    line1,
    line2: line2 || undefined,
    city,
    state,
    postalCode,
    countryCode,
    formattedAddress: formatPostalAddress({ line1, line2, city, state, postalCode }),
    validationStatus: 'needsValidation',
    latitude: address.latitude,
    longitude: address.longitude,
  };

  if (!isPostalAddressComplete(base)) {
    return {
      ...base,
      validationStatus: 'needsValidation',
      validationMessage: 'Street, city, state, and ZIP code are required for U.S. first-class mail.',
    };
  }

  if (!ZIP_REGEX.test(postalCode) || !STATE_REGEX.test(state)) {
    return {
      ...base,
      validationStatus: 'validationWarning',
      validationMessage: 'Postal address needs review to meet USPS formatting standards.',
    };
  }

  return base;
}

/**
 * @process repairPostalAddress
 * Steps:
 * 1. Return undefined for empty/missing input or when every mail field is blank.
 * 2. Defensively coerce each field to a trimmed string (legacy/imported records
 *    may carry numbers or nulls, e.g. postalCode stored as a number).
 * 3. Preserve unknown keys (careOf, urbanization, originalAddress, vendor
 *    metadata) verbatim — repair must never drop imported fields.
 * 4. Keep an already-valid validationStatus; stamp 'needsValidation' when the
 *    stored value is absent or not a known status (the common legacy case).
 * 5. Default countryCode to 'US'.
 * Inputs: a raw stored mailingAddress object of unknown provenance.
 * Outputs: a contract-valid PostalAddress (with unknown fields preserved), or
 *   undefined when there is no address content to keep.
 * Failure Handling: Never throws; malformed fields coerce to strings.
 * Idempotency: REQUIRED — re-running on an already-valid address is a no-op.
 * Notes: Used at read/hydration time (panel adapters, form initial values) and
 *   at write time (contact update services) so legacy imported addresses that
 *   predate the validationStatus contract remain editable instead of failing
 *   form validation invisibly.
 */
export function repairPostalAddress(raw: unknown): PostalAddress | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const input = raw as Record<string, unknown>;
  const str = (v: unknown): string => (v == null ? '' : String(v)).trim();
  const num = (v: unknown): number | undefined =>
    typeof v === 'number' && Number.isFinite(v) ? v : undefined;

  const line1 = str(input.line1);
  const line2 = str(input.line2);
  const city = str(input.city);
  const state = str(input.state).toUpperCase();
  const postalCode = str(input.postalCode);
  if (!line1 && !line2 && !city && !state && !postalCode) return undefined;

  const status = POSTAL_VALIDATION_STATUSES.includes(
    input.validationStatus as PostalAddressValidationStatus,
  )
    ? (input.validationStatus as PostalAddressValidationStatus)
    : 'needsValidation';

  const repaired: PostalAddress = {
    ...input,
    line1,
    line2: line2 || undefined,
    city,
    state,
    postalCode,
    countryCode: str(input.countryCode).toUpperCase() || 'US',
    formattedAddress: typeof input.formattedAddress === 'string' ? input.formattedAddress : undefined,
    validationStatus: status,
    validationMessage:
      typeof input.validationMessage === 'string' ? input.validationMessage : undefined,
    validatedAt: typeof input.validatedAt === 'string' ? input.validatedAt : undefined,
    latitude: num(input.latitude),
    longitude: num(input.longitude),
  };
  return repaired;
}

/** Mail-relevant fields compared when deciding whether to surface a suggestion. */
const COMPARED_FIELDS = ['line1', 'line2', 'city', 'state', 'postalCode'] as const;

/**
 * Normalize one address field for comparison: drop cosmetic punctuation
 * (periods/commas, e.g. "St." vs "ST"), collapse whitespace, and uppercase.
 * Mail-significant characters like the ZIP+4 hyphen are preserved so real
 * differences (e.g. "85001" vs "85001-1234") still surface a suggestion.
 */
function normalizeForCompare(value?: string): string {
  return (value ?? '')
    .replace(/[.,]/g, '')
    .trim()
    .replace(/\s+/g, ' ')
    .toUpperCase();
}

/**
 * @process diffPostalAddresses
 * Steps:
 * 1. Normalize each mail-relevant field (line1/line2/city/state/postalCode) of
 *    both addresses (trim, collapse internal whitespace, uppercase).
 * 2. Return the list of field keys whose normalized values differ.
 * Inputs: the address the user entered/picked and a candidate (e.g. USPS) result.
 * Outputs: array of differing field keys (empty when the two are equivalent).
 * Failure Handling: Pure; never throws; ignores non-mail fields (geo, status).
 * Idempotency: REQUIRED (pure function).
 * Notes: ZIP+4 enrichment (e.g. 85001 → 85001-1234) counts as a difference, so a
 *   standardized suggestion is surfaced rather than silently applied.
 */
export function diffPostalAddresses(
  entered: Partial<PostalAddress> | undefined,
  candidate: Partial<PostalAddress> | undefined,
): Array<(typeof COMPARED_FIELDS)[number]> {
  return COMPARED_FIELDS.filter(
    (field) => normalizeForCompare(entered?.[field]) !== normalizeForCompare(candidate?.[field]),
  );
}

/**
 * @process postalAddressesDiffer
 * Steps:
 * 1. Delegate to diffPostalAddresses and report whether any field changed.
 * Inputs: the entered/picked address and a candidate (e.g. USPS) result.
 * Outputs: true when the candidate differs in any mail-relevant field.
 * Failure Handling: Pure; never throws.
 * Idempotency: REQUIRED (pure function).
 */
export function postalAddressesDiffer(
  entered: Partial<PostalAddress> | undefined,
  candidate: Partial<PostalAddress> | undefined,
): boolean {
  return diffPostalAddresses(entered, candidate).length > 0;
}

/**
 * @contract MailRecipientAddress
 * Purpose: Thin address shape used by the physical-mail dialog (SendMailDialog).
 *   Intentionally matches the `MailAddress` contract in local-db/mailTypes.ts so
 *   that the result can be passed directly as `initialRecipient`.
 * Fields:
 * - name: Recipient display name (required for addressing).
 * - company: Optional company / organization name.
 * - line1..country: Standard postal fields, matching PostalAddress naming.
 * Rules: Pure data transfer — no validation or enrichment.
 * Sync: Not persisted; built on demand from a contact's PostalAddress.
 */
export interface MailRecipientAddress {
  name: string;
  company?: string;
  line1: string;
  line2?: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

/**
 * @process postalAddressToMailAddress
 * Steps:
 * 1. Map PostalAddress fields to the MailAddress shape, attaching recipient name
 *    and optional company for physical-mail addressing.
 * Inputs: A PostalAddress (or partial), plus the recipient's display name and
 *   optional company.
 * Outputs: A MailRecipientAddress ready to pre-fill SendMailDialog.
 * Failure Handling: Pure; never throws. Missing fields default to empty strings.
 * Idempotency: REQUIRED (pure function).
 */
export function postalAddressToMailAddress(
  address: Partial<PostalAddress> | undefined,
  name: string,
  company?: string,
): MailRecipientAddress {
  return {
    name,
    company,
    line1: address?.line1 ?? '',
    line2: address?.line2,
    city: address?.city ?? '',
    state: address?.state ?? '',
    postalCode: address?.postalCode ?? '',
    country: address?.countryCode ?? 'US',
  };
}
